/**
 * Shared Accessory Drawing System
 *
 * This module provides a unified accessory drawing system that works with both
 * Canvas 2D (for previews) and PixiJS Graphics (for in-game rendering).
 *
 * The key insight is that both APIs have similar drawing methods, so we can
 * abstract them behind a common interface.
 */

/**
 * Accessory type enum - the source of truth for all accessory types
 */
export enum AccessoryType {
	NONE = 'none',
	HEADPHONES = 'headphones',
	PARTY_HAT = 'party_hat',
	CROWN = 'crown',
	SUNGLASSES = 'sunglasses',
	BOW_TIE = 'bow_tie',
	HALO = 'halo',
	DEVIL_HORNS = 'devil_horns',
	BANDANA = 'bandana',
	WIZARD_HAT = 'wizard_hat',
	ANTENNA = 'antenna',
	TOP_HAT = 'top_hat',
	FLOWER_CROWN = 'flower_crown',
	PIRATE_HAT = 'pirate_hat',
	VIKING_HELMET = 'viking_helmet',
	CAT_EARS = 'cat_ears',
	BUNNY_EARS = 'bunny_ears',
	COWBOY_HAT = 'cowboy_hat',
	STAR = 'star',
	NINJA_MASK = 'ninja_mask',
	CHEF_HAT = 'chef_hat',
	HEART = 'heart',
	// Eyes Group
	GLASSES_3D = 'glasses_3d',
	MONOCLE = 'monocle',
	VR_HEADSET = 'vr_headset',
	SKI_GOGGLES = 'ski_goggles',
	AVIATORS = 'aviators',
	CYCLOPS_VISOR = 'cyclops_visor',
	EYE_PATCH = 'eye_patch',
	CUCUMBER_SLICES = 'cucumber_slices',
	ANIME_EYES = 'anime_eyes',
	// Mouth Group
	VAMPIRE_FANGS = 'vampire_fangs',
	BUCK_TEETH = 'buck_teeth',
	MUSTACHE = 'mustache',
	BEARD = 'beard',
	MEDICAL_MASK = 'medical_mask',
	CLOWN_NOSE = 'clown_nose',
	BUBBLEGUM = 'bubblegum',
	PIPE = 'pipe',
	ZIPPER_MOUTH = 'zipper_mouth',
	DOG_TONGUE = 'dog_tongue',
	// New hats
	BASEBALL_CAP = 'baseball_cap',
	BEANIE = 'beanie',
	PROPELLER_HAT = 'propeller_hat',
	GRADUATION_CAP = 'graduation_cap',
	HARD_HAT = 'hard_hat',
	FEZ = 'fez',
	BERET = 'beret',
	SOMBRERO = 'sombrero',
	SANTA_HAT = 'santa_hat',
	SAILOR_HAT = 'sailor_hat',
	FEDORA = 'fedora',
	MUSHROOM_CAP = 'mushroom_cap',
	KNIGHT_HELMET = 'knight_helmet',
	// Even more hats
	ASTRONAUT_HELMET = 'astronaut_helmet',
	JESTER_HAT = 'jester_hat',
	STRAW_HAT = 'straw_hat',
	USHANKA = 'ushanka',
	MOHAWK = 'mohawk',
	TIARA = 'tiara',
	BOWLER_HAT = 'bowler_hat',
	FOOTBALL_HELMET = 'football_helmet',
	AFRO = 'afro',
	SHOWER_CAP = 'shower_cap',
}

/**
 * Accessory placement categories - determines positioning relative to worm head
 *
 * Coordinate system (worm facing right, angle=0):
 * - 'hats': Behind head, opposite of face direction (drawn at -Y, appears on back)
 * - 'face': On the face, generic (deprecated)
 * - 'eyes': On the eyes area (drawn at +X direction)
 * - 'mouth': On the mouth/chin area (drawn at +X direction)
 * - 'top': On top of head perpendicular to body (drawn at -Y, above head)
 * - 'neck': Behind head near neck area (for bow ties, scarves)
 * - 'body': Applied to body segments (for scales, patterns - future)
 */
export type AccessoryCategory = 'hats' | 'face' | 'eyes' | 'mouth' | 'top' | 'neck' | 'body';

/**
 * Positioning configuration for each category
 * rotationOffset: Additional rotation in radians relative to head rotation
 * - 0 = accessory drawn in local coords, rotated with head
 * - PI/2 = rotated 90° clockwise from head direction
 */
export interface CategoryPosition {
	/** Rotation offset from head direction (radians) */
	rotationOffset: number;
	/** Description of where this appears */
	description: string;
}

/**
 * Positioning configs for each accessory category
 */
export const CATEGORY_POSITIONS: Record<AccessoryCategory, CategoryPosition> = {
	hats: {
		rotationOffset: -Math.PI / 2, // Hats appear on back/top of head (behind face)
		description: 'On top/back of head',
	},
	face: {
		rotationOffset: 0, // Legacy Face items
		description: 'On face',
	},
	eyes: {
		rotationOffset: 0, // Eyes area
		description: 'Eyes area',
	},
	mouth: {
		rotationOffset: 0, // Mouth area
		description: 'Mouth/chin area',
	},
	top: {
		rotationOffset: -Math.PI / 2, // Top items like ears, halos - same as hats
		description: 'Top of head',
	},
	neck: {
		rotationOffset: Math.PI, // Neck items appear at neck/back area
		description: 'Neck/chin area',
	},
	body: {
		rotationOffset: Math.PI, // Body decorations (future)
		description: 'Body segments',
	},
};

/**
 * Accessory display info
 */
export interface AccessoryInfo {
	type: AccessoryType;
	name: string;
	icon: string;
	category: AccessoryCategory;
}

/**
 * All available accessories with their display info
 */
export const ACCESSORY_LIST: AccessoryInfo[] = [
	{ type: AccessoryType.NONE, name: 'None', icon: '❌', category: 'hats' },
	// Hats - positioned on back/top of head
	{ type: AccessoryType.CROWN, name: 'Crown', icon: '👑', category: 'hats' },
	{ type: AccessoryType.PARTY_HAT, name: 'Party Hat', icon: '🥳', category: 'hats' },
	{ type: AccessoryType.WIZARD_HAT, name: 'Wizard Hat', icon: '🔮', category: 'hats' },
	{ type: AccessoryType.TOP_HAT, name: 'Top Hat', icon: '🎩', category: 'hats' },
	{ type: AccessoryType.PIRATE_HAT, name: 'Pirate', icon: '☠️', category: 'hats' },
	{ type: AccessoryType.VIKING_HELMET, name: 'Viking', icon: '🪓', category: 'hats' },
	{ type: AccessoryType.COWBOY_HAT, name: 'Cowboy', icon: '🤠', category: 'hats' },
	{ type: AccessoryType.CHEF_HAT, name: 'Chef Hat', icon: '👨‍🍳', category: 'hats' },
	{ type: AccessoryType.BANDANA, name: 'Bandana', icon: '🧣', category: 'hats' },
	{ type: AccessoryType.BASEBALL_CAP, name: 'Baseball Cap', icon: '🧢', category: 'hats' },
	{ type: AccessoryType.BEANIE, name: 'Beanie', icon: '🎿', category: 'hats' },
	{ type: AccessoryType.PROPELLER_HAT, name: 'Propeller', icon: '🚁', category: 'hats' },
	{ type: AccessoryType.GRADUATION_CAP, name: 'Graduate', icon: '🎓', category: 'hats' },
	{ type: AccessoryType.HARD_HAT, name: 'Hard Hat', icon: '👷', category: 'hats' },
	{ type: AccessoryType.FEZ, name: 'Fez', icon: '🔴', category: 'hats' },
	{ type: AccessoryType.BERET, name: 'Beret', icon: '🎨', category: 'hats' },
	{ type: AccessoryType.SOMBRERO, name: 'Sombrero', icon: '🪇', category: 'hats' },
	{ type: AccessoryType.SANTA_HAT, name: 'Santa Hat', icon: '🎅', category: 'hats' },
	{ type: AccessoryType.SAILOR_HAT, name: 'Sailor', icon: '⚓', category: 'hats' },
	{ type: AccessoryType.FEDORA, name: 'Fedora', icon: '🕵️', category: 'hats' },
	{ type: AccessoryType.MUSHROOM_CAP, name: 'Mushroom', icon: '🍄', category: 'hats' },
	{ type: AccessoryType.KNIGHT_HELMET, name: 'Knight', icon: '⚔️', category: 'hats' },
	{ type: AccessoryType.ASTRONAUT_HELMET, name: 'Astronaut', icon: '🚀', category: 'hats' },
	{ type: AccessoryType.JESTER_HAT, name: 'Jester', icon: '🃏', category: 'hats' },
	{ type: AccessoryType.STRAW_HAT, name: 'Straw Hat', icon: '👒', category: 'hats' },
	{ type: AccessoryType.USHANKA, name: 'Ushanka', icon: '🐻', category: 'hats' },
	{ type: AccessoryType.MOHAWK, name: 'Mohawk', icon: '🎸', category: 'hats' },
	{ type: AccessoryType.TIARA, name: 'Tiara', icon: '👸', category: 'hats' },
	{ type: AccessoryType.BOWLER_HAT, name: 'Bowler', icon: '🎱', category: 'hats' },
	{ type: AccessoryType.FOOTBALL_HELMET, name: 'Football', icon: '🏈', category: 'hats' },
	{ type: AccessoryType.AFRO, name: 'Afro', icon: '🪩', category: 'hats' },
	{ type: AccessoryType.SHOWER_CAP, name: 'Shower Cap', icon: '🚿', category: 'hats' },
	// Face/Eyes accessories
	{ type: AccessoryType.SUNGLASSES, name: 'Shades', icon: '🕶️', category: 'eyes' },
	{ type: AccessoryType.HEADPHONES, name: 'Headphones', icon: '🎧', category: 'eyes' }, // Moved to generic face/ears area
	{ type: AccessoryType.NINJA_MASK, name: 'Ninja', icon: '🥷', category: 'mouth' },
	// New Eyes Group
	{ type: AccessoryType.GLASSES_3D, name: '3D Glasses', icon: '👓', category: 'eyes' },
	{ type: AccessoryType.MONOCLE, name: 'Monocle', icon: '🧐', category: 'eyes' },
	{ type: AccessoryType.VR_HEADSET, name: 'VR Headset', icon: '🥽', category: 'eyes' },
	{ type: AccessoryType.SKI_GOGGLES, name: 'Ski Goggles', icon: '🎿', category: 'eyes' },
	{ type: AccessoryType.AVIATORS, name: 'Aviators', icon: '🕶️', category: 'eyes' },
	{ type: AccessoryType.CYCLOPS_VISOR, name: 'Cyclops', icon: '🔴', category: 'eyes' },
	{ type: AccessoryType.EYE_PATCH, name: 'Eye Patch', icon: '👁️', category: 'eyes' },
	{ type: AccessoryType.CUCUMBER_SLICES, name: 'Spa Day', icon: '🥒', category: 'eyes' },
	{ type: AccessoryType.ANIME_EYES, name: 'Anime', icon: '🥺', category: 'eyes' },
	// New Mouth Group
	{ type: AccessoryType.VAMPIRE_FANGS, name: 'Fangs', icon: '🧛', category: 'mouth' },
	{ type: AccessoryType.BUCK_TEETH, name: 'Buck Teeth', icon: '🤓', category: 'mouth' },
	{ type: AccessoryType.MUSTACHE, name: 'Mustache', icon: '🥸', category: 'mouth' },
	{ type: AccessoryType.BEARD, name: 'Beard', icon: '🎅', category: 'mouth' },
	{ type: AccessoryType.MEDICAL_MASK, name: 'Mask', icon: '😷', category: 'mouth' },
	{ type: AccessoryType.CLOWN_NOSE, name: 'Clown Nose', icon: '🤡', category: 'mouth' },
	{ type: AccessoryType.BUBBLEGUM, name: 'Bubblegum', icon: '🫧', category: 'mouth' },
	{ type: AccessoryType.PIPE, name: 'Pipe', icon: '🚬', category: 'mouth' },
	{ type: AccessoryType.ZIPPER_MOUTH, name: 'Quiet', icon: '🤐', category: 'mouth' },
	{ type: AccessoryType.DOG_TONGUE, name: 'Dog', icon: '🐶', category: 'mouth' },
	// Top accessories - on top of head (ears, horns, halos)
	{ type: AccessoryType.CAT_EARS, name: 'Cat Ears', icon: '🐱', category: 'top' },
	{ type: AccessoryType.BUNNY_EARS, name: 'Bunny Ears', icon: '🐰', category: 'top' },
	{ type: AccessoryType.DEVIL_HORNS, name: 'Devil', icon: '😈', category: 'top' },
	{ type: AccessoryType.HALO, name: 'Halo', icon: '😇', category: 'top' },
	{ type: AccessoryType.ANTENNA, name: 'Antenna', icon: '👽', category: 'top' },
	{ type: AccessoryType.FLOWER_CROWN, name: 'Flowers', icon: '🌸', category: 'top' },
	{ type: AccessoryType.STAR, name: 'Star', icon: '⭐', category: 'top' },
	{ type: AccessoryType.HEART, name: 'Heart', icon: '💖', category: 'top' },
	// Neck accessories - positioned at neck/chin area
	{ type: AccessoryType.BOW_TIE, name: 'Bow Tie', icon: '🎀', category: 'neck' },
];

/**
 * Get the category for an accessory type
 */
export function getAccessoryCategory(type: AccessoryType): AccessoryCategory {
	const info = ACCESSORY_LIST.find(a => a.type === type);
	return info?.category ?? 'hats';
}

/**
 * Get positioning config for an accessory type
 */
export function getAccessoryPosition(type: AccessoryType): CategoryPosition {
	const category = getAccessoryCategory(type);
	return CATEGORY_POSITIONS[category];
}

/**
 * Check if accessory is a face accessory (vs hat/other)
 * @deprecated Use getAccessoryCategory(type) === 'face' instead
 */
export function isFaceAccessory(type: AccessoryType): boolean {
	return getAccessoryCategory(type) === 'face';
}

/**
 * Abstract drawing context interface
 * Both Canvas 2D and PixiJS Graphics can implement this
 */
export interface DrawingContext {
	// Path operations
	beginPath(): void;
	closePath(): void;
	moveTo(x: number, y: number): void;
	lineTo(x: number, y: number): void;
	quadraticCurveTo(cpx: number, cpy: number, x: number, y: number): void;
	bezierCurveTo(cp1x: number, cp1y: number, cp2x: number, cp2y: number, x: number, y: number): void;
	arc(x: number, y: number, radius: number, startAngle: number, endAngle: number, counterclockwise?: boolean): void;
	ellipse(x: number, y: number, radiusX: number, radiusY: number, rotation: number, startAngle: number, endAngle: number): void;
	roundRect(x: number, y: number, width: number, height: number, radius: number): void;
	rect(x: number, y: number, width: number, height: number): void;

	// Fill and stroke
	fill(color: number | string, alpha?: number): void;
	stroke(color: number | string, width: number, alpha?: number): void;

	// Circle helper (common operation)
	circle(x: number, y: number, radius: number): void;
}

/**
 * Canvas 2D adapter for DrawingContext
 */
export class Canvas2DAdapter implements DrawingContext {
	private ctx: CanvasRenderingContext2D;

	constructor(ctx: CanvasRenderingContext2D) {
		this.ctx = ctx;
	}

	beginPath(): void {
		this.ctx.beginPath();
	}

	closePath(): void {
		this.ctx.closePath();
	}

	moveTo(x: number, y: number): void {
		this.ctx.moveTo(x, y);
	}

	lineTo(x: number, y: number): void {
		this.ctx.lineTo(x, y);
	}

	quadraticCurveTo(cpx: number, cpy: number, x: number, y: number): void {
		this.ctx.quadraticCurveTo(cpx, cpy, x, y);
	}

	bezierCurveTo(cp1x: number, cp1y: number, cp2x: number, cp2y: number, x: number, y: number): void {
		this.ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y);
	}

	arc(x: number, y: number, radius: number, startAngle: number, endAngle: number, counterclockwise?: boolean): void {
		this.ctx.arc(x, y, radius, startAngle, endAngle, counterclockwise);
	}

	ellipse(x: number, y: number, radiusX: number, radiusY: number, rotation: number, startAngle: number, endAngle: number): void {
		this.ctx.ellipse(x, y, radiusX, radiusY, rotation, startAngle, endAngle);
	}

	roundRect(x: number, y: number, width: number, height: number, radius: number): void {
		this.ctx.roundRect(x, y, width, height, radius);
	}

	rect(x: number, y: number, width: number, height: number): void {
		this.ctx.rect(x, y, width, height);
	}

	fill(color: number | string, alpha = 1): void {
		if (typeof color === 'number') {
			color = '#' + color.toString(16).padStart(6, '0');
		}
		if (alpha < 1) {
			this.ctx.fillStyle = this.addAlpha(color, alpha);
		} else {
			this.ctx.fillStyle = color;
		}
		this.ctx.fill();
	}

	stroke(color: number | string, width: number, alpha = 1): void {
		if (typeof color === 'number') {
			color = '#' + color.toString(16).padStart(6, '0');
		}
		if (alpha < 1) {
			this.ctx.strokeStyle = this.addAlpha(color, alpha);
		} else {
			this.ctx.strokeStyle = color;
		}
		this.ctx.lineWidth = width;
		this.ctx.stroke();
	}

	circle(x: number, y: number, radius: number): void {
		this.beginPath();
		this.arc(x, y, radius, 0, Math.PI * 2);
	}

	private addAlpha(color: string, alpha: number): string {
		if (color.startsWith('#')) {
			const r = parseInt(color.slice(1, 3), 16);
			const g = parseInt(color.slice(3, 5), 16);
			const b = parseInt(color.slice(5, 7), 16);
			return `rgba(${r}, ${g}, ${b}, ${alpha})`;
		}
		return color;
	}
}

// =============================================================================
// ACCESSORY DRAWING FUNCTIONS
// These use the abstract DrawingContext so they work with both Canvas and PixiJS
// =============================================================================

/** Draw a 5-pointed star */
export function drawStar(dc: DrawingContext, x: number, y: number, size: number, color: number | string): void {
	const points = 5;
	const outerRadius = size;
	const innerRadius = size * 0.4;

	dc.beginPath();
	for (let i = 0; i < points * 2; i++) {
		const radius = i % 2 === 0 ? outerRadius : innerRadius;
		const angle = (i * Math.PI) / points - Math.PI / 2;
		const px = x + Math.cos(angle) * radius;
		const py = y + Math.sin(angle) * radius;
		if (i === 0) dc.moveTo(px, py);
		else dc.lineTo(px, py);
	}
	dc.closePath();
	dc.fill(color);
}

/** Draw crown accessory */
export function drawCrown(dc: DrawingContext, r: number): void {
	// Crown shadow
	dc.beginPath();
	dc.moveTo(-r * 0.85, -r * 0.25);
	dc.lineTo(-r * 0.85, -r * 0.85);
	dc.lineTo(-r * 0.53, -r * 0.55);
	dc.lineTo(-r * 0.28, -r * 1.05);
	dc.lineTo(0, -r * 0.65);
	dc.lineTo(r * 0.28, -r * 1.05);
	dc.lineTo(r * 0.53, -r * 0.55);
	dc.lineTo(r * 0.85, -r * 0.85);
	dc.lineTo(r * 0.85, -r * 0.25);
	dc.closePath();
	dc.fill(0xb8860b);

	// Crown main body
	dc.beginPath();
	dc.moveTo(-r * 0.8, -r * 0.3);
	dc.lineTo(-r * 0.8, -r * 0.9);
	dc.lineTo(-r * 0.5, -r * 0.6);
	dc.lineTo(-r * 0.25, -r * 1.1);
	dc.lineTo(0, -r * 0.7);
	dc.lineTo(r * 0.25, -r * 1.1);
	dc.lineTo(r * 0.5, -r * 0.6);
	dc.lineTo(r * 0.8, -r * 0.9);
	dc.lineTo(r * 0.8, -r * 0.3);
	dc.closePath();
	dc.fill(0xffd700);
	dc.stroke(0xdaa520, 2);

	// Gems
	dc.circle(0, -r * 0.5, r * 0.12);
	dc.fill(0xff0000);
	dc.circle(-r * 0.4, -r * 0.45, r * 0.08);
	dc.fill(0x00ff00);
	dc.circle(r * 0.4, -r * 0.45, r * 0.08);
	dc.fill(0x0088ff);
}

/** Draw party hat accessory */
export function drawPartyHat(dc: DrawingContext, r: number): void {
	// Cone shadow
	dc.beginPath();
	dc.moveTo(-r * 0.65, -r * 0.15);
	dc.lineTo(0, -r * 2.15);
	dc.lineTo(r * 0.65, -r * 0.15);
	dc.closePath();
	dc.fill(0xcc4488);

	// Main cone
	dc.beginPath();
	dc.moveTo(-r * 0.6, -r * 0.2);
	dc.lineTo(0, -r * 2.2);
	dc.lineTo(r * 0.6, -r * 0.2);
	dc.closePath();
	dc.fill(0xff69b4);
	dc.stroke(0xff1493, 2);

	// Stripes
	dc.beginPath();
	dc.moveTo(-r * 0.52, -r * 0.45);
	dc.lineTo(r * 0.52, -r * 0.45);
	dc.stroke(0x00ffff, r * 0.12);

	dc.beginPath();
	dc.moveTo(-r * 0.42, -r * 0.8);
	dc.lineTo(r * 0.42, -r * 0.8);
	dc.stroke(0xffff00, r * 0.1);

	// Pom pom
	dc.circle(0, -r * 2.2, r * 0.22);
	dc.fill(0xffffff);
}

/** Draw wizard hat accessory */
export function drawWizardHat(dc: DrawingContext, r: number): void {
	// Brim shadow
	dc.beginPath();
	dc.ellipse(0, -r * 0.25, r * 1.25, r * 0.28, 0, 0, Math.PI * 2);
	dc.fill(0x1a0a3e);

	// Brim
	dc.beginPath();
	dc.ellipse(0, -r * 0.3, r * 1.2, r * 0.25, 0, 0, Math.PI * 2);
	dc.fill(0x2a1a5e);
	dc.stroke(0x5533aa, 2);

	// Cone shadow
	dc.beginPath();
	dc.moveTo(-r * 0.75, -r * 0.25);
	dc.quadraticCurveTo(-r * 0.55, -r * 1.45, r * 0.05, -r * 2.45);
	dc.quadraticCurveTo(r * 0.55, -r * 1.45, r * 0.75, -r * 0.25);
	dc.closePath();
	dc.fill(0x1a0a3e);

	// Cone main
	dc.beginPath();
	dc.moveTo(-r * 0.7, -r * 0.3);
	dc.quadraticCurveTo(-r * 0.5, -r * 1.5, 0, -r * 2.5);
	dc.quadraticCurveTo(r * 0.5, -r * 1.5, r * 0.7, -r * 0.3);
	dc.closePath();
	dc.fill(0x2a1a5e);
	dc.stroke(0x5533aa, 2);

	// Decorative band
	dc.beginPath();
	dc.moveTo(-r * 0.6, -r * 0.5);
	dc.quadraticCurveTo(0, -r * 0.65, r * 0.6, -r * 0.5);
	dc.stroke(0x8844cc, r * 0.12);

	// Moon crescent
	dc.beginPath();
	dc.arc(-r * 0.3, -r * 0.9, r * 0.15, 0.5, Math.PI * 1.5, false);
	dc.stroke(0xffeedd, 3);

	// Stars
	drawStar(dc, r * 0.15, -r * 1.2, r * 0.16, 0xffd700);
	drawStar(dc, r * 0.35, -r * 1.0, r * 0.1, 0xffee88);
	drawStar(dc, -r * 0.15, -r * 1.5, r * 0.14, 0xffd700);
	drawStar(dc, r * 0.1, -r * 1.85, r * 0.11, 0xffd700);

	// Sparkle dots
	dc.circle(-r * 0.4, -r * 1.3, r * 0.03);
	dc.fill(0xffffff);
	dc.circle(r * 0.3, -r * 1.6, r * 0.025);
	dc.fill(0xffffff);

	// Magical tip glow
	dc.circle(0, -r * 2.5, r * 0.08);
	dc.fill(0xffd700, 0.8);
	dc.circle(0, -r * 2.5, r * 0.05);
	dc.fill(0xffffff);
}

/** Draw top hat accessory */
export function drawTopHat(dc: DrawingContext, r: number): void {
	// Shadow under brim
	dc.beginPath();
	dc.ellipse(0, -r * 0.45, r * 1.35, r * 0.22, 0, 0, Math.PI * 2);
	dc.fill(0x0a0a0a);

	// Brim
	dc.beginPath();
	dc.ellipse(0, -r * 0.5, r * 1.3, r * 0.2, 0, 0, Math.PI * 2);
	dc.fill(0x1a1a1a);
	dc.stroke(0x333333, 2);

	// Hat body
	dc.beginPath();
	dc.roundRect(-r * 0.7, -r * 2.2, r * 1.4, r * 1.7, r * 0.1);
	dc.fill(0x1a1a1a);
	dc.stroke(0x333333, 2);

	// Red ribbon band
	dc.beginPath();
	dc.rect(-r * 0.7, -r * 0.93, r * 1.4, r * 0.24);
	dc.fill(0xcc0000);
}

/** Draw pirate hat accessory */
export function drawPirateHat(dc: DrawingContext, r: number): void {
	// Hat shadow
	dc.beginPath();
	dc.moveTo(-r * 1.25, -r * 0.25);
	dc.quadraticCurveTo(-r * 0.85, -r * 1.45, 0, -r * 1.75);
	dc.quadraticCurveTo(r * 0.85, -r * 1.45, r * 1.25, -r * 0.25);
	dc.quadraticCurveTo(r * 0.55, -r * 0.45, 0, -r * 0.25);
	dc.quadraticCurveTo(-r * 0.55, -r * 0.45, -r * 1.25, -r * 0.25);
	dc.fill(0x0a0a0a);

	// Main hat body
	dc.beginPath();
	dc.moveTo(-r * 1.2, -r * 0.3);
	dc.quadraticCurveTo(-r * 0.8, -r * 1.5, 0, -r * 1.8);
	dc.quadraticCurveTo(r * 0.8, -r * 1.5, r * 1.2, -r * 0.3);
	dc.quadraticCurveTo(r * 0.5, -r * 0.5, 0, -r * 0.3);
	dc.quadraticCurveTo(-r * 0.5, -r * 0.5, -r * 1.2, -r * 0.3);
	dc.fill(0x1a1a1a);
	dc.stroke(0x2a2a2a, 2);

	// Gold trim
	dc.beginPath();
	dc.moveTo(-r * 1.05, -r * 0.38);
	dc.quadraticCurveTo(-r * 0.65, -r * 1.15, 0, -r * 1.48);
	dc.quadraticCurveTo(r * 0.65, -r * 1.15, r * 1.05, -r * 0.38);
	dc.stroke(0xffd700, 3);

	// Skull
	dc.beginPath();
	dc.ellipse(0, -r * 0.85, r * 0.25, r * 0.22, 0, 0, Math.PI * 2);
	dc.fill(0xf5f5dc);
	// Eyes
	dc.circle(-r * 0.1, -r * 0.88, r * 0.06);
	dc.fill(0x1a1a1a);
	dc.circle(r * 0.1, -r * 0.88, r * 0.06);
	dc.fill(0x1a1a1a);
}

/** Draw viking helmet accessory */
export function drawVikingHelmet(dc: DrawingContext, r: number): void {
	// Helmet dome
	dc.beginPath();
	dc.arc(0, -r * 0.3, r * 0.9, Math.PI, 0, false);
	dc.fill(0x666666);
	dc.stroke(0x888888, 2);

	// Nose guard
	dc.beginPath();
	dc.moveTo(-r * 0.1, -r * 0.3);
	dc.lineTo(-r * 0.08, r * 0.2);
	dc.lineTo(r * 0.08, r * 0.2);
	dc.lineTo(r * 0.1, -r * 0.3);
	dc.closePath();
	dc.fill(0x777777);

	// Left horn
	dc.beginPath();
	dc.moveTo(-r * 0.7, -r * 0.5);
	dc.quadraticCurveTo(-r * 1.3, -r * 0.8, -r * 1.4, -r * 1.5);
	dc.quadraticCurveTo(-r * 1.1, -r * 0.9, -r * 0.8, -r * 0.7);
	dc.closePath();
	dc.fill(0xd2b48c);
	dc.stroke(0x8b7355, 2);

	// Right horn
	dc.beginPath();
	dc.moveTo(r * 0.7, -r * 0.5);
	dc.quadraticCurveTo(r * 1.3, -r * 0.8, r * 1.4, -r * 1.5);
	dc.quadraticCurveTo(r * 1.1, -r * 0.9, r * 0.8, -r * 0.7);
	dc.closePath();
	dc.fill(0xd2b48c);
	dc.stroke(0x8b7355, 2);
}

/** Draw cowboy hat accessory */
export function drawCowboyHat(dc: DrawingContext, r: number): void {
	// Wide brim
	dc.beginPath();
	dc.ellipse(0, -r * 0.3, r * 1.4, r * 0.25, 0, 0, Math.PI * 2);
	dc.fill(0x8b4513);
	dc.stroke(0x5d3a1a, 2);

	// Crown
	dc.beginPath();
	dc.moveTo(-r * 0.5, -r * 0.35);
	dc.quadraticCurveTo(-r * 0.6, -r * 1.0, -r * 0.35, -r * 1.2);
	dc.lineTo(r * 0.35, -r * 1.2);
	dc.quadraticCurveTo(r * 0.6, -r * 1.0, r * 0.5, -r * 0.35);
	dc.closePath();
	dc.fill(0xa0522d);

	// Band
	dc.beginPath();
	dc.rect(-r * 0.5, -r * 0.55, r * 1.0, r * 0.15);
	dc.fill(0x2f1810);
}

/** Draw chef hat accessory */
export function drawChefHat(dc: DrawingContext, r: number): void {
	// Puffy top
	dc.circle(-r * 0.3, -r * 1.4, r * 0.5);
	dc.fill(0xffffff);
	dc.circle(r * 0.3, -r * 1.4, r * 0.5);
	dc.fill(0xffffff);
	dc.circle(0, -r * 1.6, r * 0.5);
	dc.fill(0xffffff);

	// Base cylinder
	dc.beginPath();
	dc.rect(-r * 0.6, -r * 1.0, r * 1.2, r * 0.7);
	dc.fill(0xffffff);
	dc.stroke(0xdddddd, 2);
}

/** Draw heart accessory */
export function drawHeartAccessory(dc: DrawingContext, r: number): void {
	// Glow effect
	dc.circle(0, -r * 1.4, r * 0.6);
	dc.fill(0xff69b4, 0.3);

	// Heart shape
	const hx = 0;
	const hy = -r * 1.3;
	const size = r * 0.5;

	dc.beginPath();
	dc.moveTo(hx, hy + size * 0.3);
	dc.bezierCurveTo(
		hx - size * 0.5, hy - size * 0.3,
		hx - size, hy + size * 0.3,
		hx, hy + size
	);
	dc.bezierCurveTo(
		hx + size, hy + size * 0.3,
		hx + size * 0.5, hy - size * 0.3,
		hx, hy + size * 0.3
	);
	dc.fill(0xff69b4);

	// Highlight
	dc.circle(hx - size * 0.25, hy + size * 0.1, size * 0.15);
	dc.fill(0xffffff, 0.5);
}

// ==========================================
// NEW ACCESSORIES IMPLEMENTATION
// ==========================================

// --- EYES GROUP ---

export function drawGlasses3D(dc: DrawingContext, r: number): void {
	const faceX = r * 0.4;
	// Frames
	dc.beginPath();
	dc.rect(faceX, -r * 0.8, r * 0.1, r * 1.6);
	dc.fill(0xffffff); // Bridge

	// Left lens frame
	dc.beginPath();
	dc.rect(faceX, -r * 0.8, r * 0.2, r * 0.7);
	dc.fill(0xffffff);

	// Right lens frame
	dc.beginPath();
	dc.rect(faceX, r * 0.1, r * 0.2, r * 0.7);
	dc.fill(0xffffff);

	// Lenses
	dc.beginPath();
	dc.rect(faceX + r * 0.05, -r * 0.75, r * 0.15, r * 0.6);
	dc.fill(0x00ffff, 0.6); // Cyan

	dc.beginPath();
	dc.rect(faceX + r * 0.05, r * 0.15, r * 0.15, r * 0.6);
	dc.fill(0xff0000, 0.6); // Red

	// Bridge connect
	dc.beginPath();
	dc.rect(faceX + r * 0.05, -r * 0.1, r * 0.1, r * 0.2);
	dc.fill(0xffffff);
}

export function drawMonocle(dc: DrawingContext, r: number): void {
	// Single lens on right eye
	dc.beginPath();
	dc.circle(r * 0.5, r * 0.45, r * 0.3);
	dc.stroke(0xffd700, r * 0.05);
	dc.fill(0xaaddff, 0.3);

	// Chain
	dc.beginPath();
	dc.moveTo(r * 0.5, r * 0.75);
	dc.quadraticCurveTo(r * 0.2, r * 0.9, -r * 0.5, r * 0.8);
	dc.stroke(0xffd700, r * 0.03);
}

export function drawVRHeadset(dc: DrawingContext, r: number): void {
	// Boxy headset
	dc.beginPath();
	dc.roundRect(r * 0.4, -r * 0.7, r * 0.6, r * 1.4, r * 0.1);
	dc.fill(0x111111);
	dc.stroke(0x333333, 2);

	// Front lights
	dc.beginPath();
	dc.rect(r * 1.0, -r * 0.6, r * 0.05, r * 1.2);
	dc.fill(0x00ff00);

	// Straps
	dc.beginPath();
	dc.moveTo(r * 0.4, -r * 0.6);
	dc.lineTo(-r * 0.5, -r * 0.8);
	dc.stroke(0x222222, r * 0.1);

	dc.beginPath();
	dc.moveTo(r * 0.4, r * 0.6);
	dc.lineTo(-r * 0.5, r * 0.8);
	dc.stroke(0x222222, r * 0.1);
}

export function drawSkiGoggles(dc: DrawingContext, r: number): void {
	// Large visor
	dc.beginPath();
	dc.roundRect(r * 0.3, -r * 0.8, r * 0.5, r * 1.6, r * 0.2);
	dc.fill(0xffaa00, 0.8);
	dc.stroke(0x333333, r * 0.1);

	// Reflection
	dc.beginPath();
	dc.moveTo(r * 0.4, -r * 0.6);
	dc.lineTo(r * 0.5, r * 0.6);
	dc.stroke(0xffffff, r * 0.15, 0.4);
}

export function drawAviators(dc: DrawingContext, r: number): void {
	const wireColor = 0xdddddd;
	const lensColor = 0x222222;

	// Left lens
	dc.beginPath();
	dc.moveTo(r * 0.5, -r * 0.1);
	dc.bezierCurveTo(r * 0.8, -r * 0.1, r * 0.8, -r * 0.8, r * 0.5, -r * 0.8);
	dc.bezierCurveTo(r * 0.3, -r * 0.8, r * 0.3, -r * 0.4, r * 0.5, -r * 0.1);
	dc.fill(lensColor, 0.9);
	dc.stroke(wireColor, r * 0.03);

	// Right lens
	dc.beginPath();
	dc.moveTo(r * 0.5, r * 0.1);
	dc.bezierCurveTo(r * 0.8, r * 0.1, r * 0.8, r * 0.8, r * 0.5, r * 0.8);
	dc.bezierCurveTo(r * 0.3, r * 0.8, r * 0.3, r * 0.4, r * 0.5, r * 0.1);
	dc.fill(lensColor, 0.9);
	dc.stroke(wireColor, r * 0.03);

	// Bridge
	dc.beginPath();
	dc.moveTo(r * 0.5, -r * 0.1);
	dc.lineTo(r * 0.5, r * 0.1);
	dc.stroke(wireColor, r * 0.03);

	dc.beginPath();
	dc.moveTo(r * 0.55, -r * 0.1);
	dc.lineTo(r * 0.55, r * 0.1);
	dc.stroke(wireColor, r * 0.03);
}

export function drawCyclopsVisor(dc: DrawingContext, r: number): void {
	// Visor body
	dc.beginPath();
	dc.rect(r * 0.4, -r * 0.85, r * 0.4, r * 1.7);
	dc.fill(0xddbb00);

	// Laser slit
	dc.beginPath();
	dc.rect(r * 0.55, -r * 0.8, r * 0.1, r * 1.6);
	dc.fill(0xff0000, 0.9);

	// Glow
	dc.beginPath();
	dc.rect(r * 0.6, -r * 0.8, r * 0.05, r * 1.6);
	dc.fill(0xff8888, 0.5);
}

export function drawEyePatch(dc: DrawingContext, r: number): void {
	// Patch on Left Eye
	dc.beginPath();
	dc.circle(r * 0.55, -r * 0.45, r * 0.35);
	dc.fill(0x111111);

	// Strap
	dc.beginPath();
	dc.moveTo(r * 0.55, -r * 0.45);
	dc.lineTo(-r * 0.5, r * 0.5);
	dc.stroke(0x111111, r * 0.08);
}

export function drawCucumberSlices(dc: DrawingContext, r: number): void {
	const drawSlice = (y: number) => {
		dc.beginPath();
		dc.circle(r * 0.6, y, r * 0.35);
		dc.fill(0x90ee90);
		dc.stroke(0x228b22, r * 0.05);

		// Seed
		dc.circle(r * 0.6, y, r * 0.1);
		dc.fill(0xffffff, 0.5);
	};

	drawSlice(-r * 0.45);
	drawSlice(r * 0.45);
}

export function drawAnimeEyes(dc: DrawingContext, r: number): void {
	const drawEye = (y: number) => {
		dc.beginPath();
		dc.ellipse(r * 0.6, y, r * 0.3, r * 0.4, 0, 0, Math.PI * 2);
		dc.fill(0x000000);

		// Top shine
		dc.circle(r * 0.65, y - r * 0.15, r * 0.12);
		dc.fill(0xffffff);

		// Bottom shine
		dc.circle(r * 0.58, y + r * 0.15, r * 0.08);
		dc.fill(0xffffff);

		// Blush
		dc.beginPath();
		dc.ellipse(r * 0.5, y + r * 0.5 * (y > 0 ? 1 : -1), r * 0.15, r * 0.08, 0, 0, Math.PI * 2);
		dc.fill(0xffaaaa, 0.5);
	};

	drawEye(-r * 0.45);
	drawEye(r * 0.45);
}

// --- MOUTH GROUP ---

export function drawVampireFangs(dc: DrawingContext, r: number): void {
	// Mouth line
	dc.beginPath();
	dc.moveTo(r * 0.5, -r * 0.3);
	dc.quadraticCurveTo(r * 0.6, 0, r * 0.5, r * 0.3);
	dc.stroke(0x000000, r * 0.05);

	// Fangs
	dc.beginPath();
	dc.moveTo(r * 0.55, -r * 0.2);
	dc.lineTo(r * 0.8, -r * 0.2);
	dc.lineTo(r * 0.6, -r * 0.1);
	dc.fill(0xffffff);

	dc.beginPath();
	dc.moveTo(r * 0.55, r * 0.2);
	dc.lineTo(r * 0.8, r * 0.2);
	dc.lineTo(r * 0.6, r * 0.1);
	dc.fill(0xffffff);
}

export function drawBuckTeeth(dc: DrawingContext, r: number): void {
	dc.beginPath();
	dc.rect(r * 0.5, -r * 0.15, r * 0.25, r * 0.14);
	dc.fill(0xffffff);
	dc.stroke(0xcccccc, 1);

	dc.beginPath();
	dc.rect(r * 0.5, 0.01, r * 0.25, r * 0.14);
	dc.fill(0xffffff);
	dc.stroke(0xcccccc, 1);
}

export function drawMustache(dc: DrawingContext, r: number): void {
	dc.beginPath();
	dc.moveTo(r * 0.5, 0);
	// Left side
	dc.bezierCurveTo(r * 0.6, -r * 0.1, r * 0.7, -r * 0.5, r * 0.4, -r * 0.6);
	dc.quadraticCurveTo(r * 0.5, -r * 0.4, r * 0.5, -r * 0.1);
	// Right side
	dc.moveTo(r * 0.5, 0);
	dc.bezierCurveTo(r * 0.6, r * 0.1, r * 0.7, r * 0.5, r * 0.4, r * 0.6);
	dc.quadraticCurveTo(r * 0.5, r * 0.4, r * 0.5, r * 0.1);

	dc.fill(0x332211);
}

export function drawBeard(dc: DrawingContext, r: number): void {
	dc.beginPath();
	dc.moveTo(r * 0.4, -r * 0.5);
	dc.quadraticCurveTo(r * 0.8, -r * 0.3, r * 0.9, 0);
	dc.quadraticCurveTo(r * 0.8, r * 0.3, r * 0.4, r * 0.5);

	dc.quadraticCurveTo(0, r * 0.4, -r * 0.2, 0);
	dc.quadraticCurveTo(0, -r * 0.4, r * 0.4, -r * 0.5);

	dc.fill(0xeeeeee);
}

export function drawMedicalMask(dc: DrawingContext, r: number): void {
	dc.beginPath();
	dc.roundRect(r * 0.4, -r * 0.5, r * 0.4, r * 1.0, r * 0.1);
	dc.fill(0x88ccff);

	// Straps
	dc.beginPath();
	dc.moveTo(r * 0.4, -r * 0.4);
	dc.lineTo(-r * 0.2, -r * 0.6);
	dc.stroke(0xffffff, 2);

	dc.beginPath();
	dc.moveTo(r * 0.4, r * 0.4);
	dc.lineTo(-r * 0.2, r * 0.6);
	dc.stroke(0xffffff, 2);
}

export function drawClownNose(dc: DrawingContext, r: number): void {
	dc.circle(r * 0.9, 0, r * 0.25);
	dc.fill(0xff0000);
	// Shine
	dc.circle(r * 0.95, -r * 0.08, r * 0.08);
	dc.fill(0xffffff, 0.6);
}

export function drawBubblegum(dc: DrawingContext, r: number): void {
	// Simple circle for preview (no animation)
	dc.beginPath();
	dc.circle(r * 0.8, 0, r * 0.35);
	dc.fill(0xff69b4, 0.8);
	dc.stroke(0xff1493, 1);

	dc.circle(r * 0.9, -r * 0.1, r * 0.1);
	dc.fill(0xffffff, 0.5);
}

export function drawPipe(dc: DrawingContext, r: number): void {
	dc.beginPath();
	dc.moveTo(r * 0.5, r * 0.1);
	dc.lineTo(r * 0.9, r * 0.4);
	dc.stroke(0x654321, r * 0.08);

	dc.circle(r * 0.9, r * 0.4, r * 0.15);
	dc.fill(0x331100);
}

export function drawZipperMouth(dc: DrawingContext, r: number): void {
	// Line
	dc.beginPath();
	dc.moveTo(r * 0.5, -r * 0.3);
	dc.lineTo(r * 0.5, r * 0.3);
	dc.stroke(0x333333, r * 0.08);

	// Pull
	dc.beginPath();
	dc.rect(r * 0.45, 0, r * 0.05, r * 0.2);
	dc.fill(0x888888);

	// Teeth
	dc.beginPath();
	for (let i = -3; i <= 3; i++) {
		dc.moveTo(r * 0.45, i * r * 0.08);
		dc.lineTo(r * 0.55, i * r * 0.08);
	}
	dc.stroke(0xaaaaaa, 1);
}

export function drawDogTongue(dc: DrawingContext, r: number): void {
	dc.beginPath();
	dc.moveTo(r * 0.5, 0);
	dc.quadraticCurveTo(r * 0.8, 0, r * 0.8, r * 0.3);
	dc.quadraticCurveTo(r * 0.8, r * 0.5, r * 0.6, r * 0.5);
	dc.quadraticCurveTo(r * 0.5, r * 0.4, r * 0.5, r * 0.2);
	dc.fill(0xff9999);
	dc.stroke(0xcc6666, 1);
}

/** Draw bandana accessory */
export function drawBandana(dc: DrawingContext, r: number): void {
	// Bandana wrap
	dc.beginPath();
	dc.arc(0, 0, r * 0.95, -Math.PI * 0.8, Math.PI * 0.8, true);
	dc.stroke(0xcc0000, r * 0.25);

	// Knot
	dc.circle(-r * 0.7, -r * 0.5, r * 0.2);
	dc.fill(0xcc0000);

	// Tails
	dc.beginPath();
	dc.moveTo(-r * 0.85, -r * 0.4);
	dc.quadraticCurveTo(-r * 1.2, -r * 0.1, -r * 1.3, r * 0.3);
	dc.stroke(0xcc0000, r * 0.15);
}

/** Draw sunglasses accessory */
export function drawSunglasses(dc: DrawingContext, r: number): void {
	// Bridge
	dc.beginPath();
	dc.moveTo(-r * 0.15, 0);
	dc.lineTo(r * 0.15, 0);
	dc.stroke(0x333333, r * 0.08);

	// Left lens
	dc.beginPath();
	dc.ellipse(-r * 0.5, 0, r * 0.4, r * 0.3, 0, 0, Math.PI * 2);
	dc.fill(0x1a1a1a);
	dc.stroke(0x333333, 2);

	// Right lens
	dc.beginPath();
	dc.ellipse(r * 0.5, 0, r * 0.4, r * 0.3, 0, 0, Math.PI * 2);
	dc.fill(0x1a1a1a);
	dc.stroke(0x333333, 2);

	// Lens shine
	dc.beginPath();
	dc.ellipse(-r * 0.55, -r * 0.08, r * 0.12, r * 0.08, -0.3, 0, Math.PI * 2);
	dc.fill(0xffffff, 0.3);
}

/** Draw headphones accessory */
export function drawHeadphones(dc: DrawingContext, r: number): void {
	// Headband
	dc.beginPath();
	dc.arc(0, r * 0.1, r * 1.1, -Math.PI * 0.8, -Math.PI * 0.2, false);
	dc.stroke(0x333333, r * 0.15);

	// Left ear cup
	dc.circle(-r * 0.85, r * 0.3, r * 0.4);
	dc.fill(0x222222);
	dc.circle(-r * 0.85, r * 0.3, r * 0.3);
	dc.fill(0x444444);

	// Right ear cup
	dc.circle(r * 0.85, r * 0.3, r * 0.4);
	dc.fill(0x222222);
	dc.circle(r * 0.85, r * 0.3, r * 0.3);
	dc.fill(0x444444);
}

/** Draw ninja mask accessory */
export function drawNinjaMask(dc: DrawingContext, r: number): void {
	// Mask wrap
	dc.circle(0, 0, r * 0.9);
	dc.fill(0x1a1a1a);

	// Eye slit
	dc.beginPath();
	dc.moveTo(-r * 0.7, -r * 0.1);
	dc.lineTo(r * 0.7, -r * 0.1);
	dc.lineTo(r * 0.6, r * 0.15);
	dc.lineTo(-r * 0.6, r * 0.15);
	dc.closePath();
	dc.fill(0xf5deb3);
}

/** Draw cat ears accessory */
export function drawCatEars(dc: DrawingContext, r: number): void {
	// Left ear
	dc.beginPath();
	dc.moveTo(-r * 0.8, -r * 0.2);
	dc.lineTo(-r * 0.6, -r * 1.2);
	dc.lineTo(-r * 0.2, -r * 0.4);
	dc.closePath();
	dc.fill(0xffb6c1);
	dc.stroke(0xff69b4, 2);

	// Right ear
	dc.beginPath();
	dc.moveTo(r * 0.8, -r * 0.2);
	dc.lineTo(r * 0.6, -r * 1.2);
	dc.lineTo(r * 0.2, -r * 0.4);
	dc.closePath();
	dc.fill(0xffb6c1);
	dc.stroke(0xff69b4, 2);

	// Inner ear pink
	dc.beginPath();
	dc.moveTo(-r * 0.65, -r * 0.35);
	dc.lineTo(-r * 0.55, -r * 0.9);
	dc.lineTo(-r * 0.35, -r * 0.45);
	dc.closePath();
	dc.fill(0xff1493);

	dc.beginPath();
	dc.moveTo(r * 0.65, -r * 0.35);
	dc.lineTo(r * 0.55, -r * 0.9);
	dc.lineTo(r * 0.35, -r * 0.45);
	dc.closePath();
	dc.fill(0xff1493);
}

/** Draw bunny ears accessory */
export function drawBunnyEars(dc: DrawingContext, r: number): void {
	// Left ear
	dc.beginPath();
	dc.ellipse(-r * 0.4, -r * 1.2, r * 0.2, r * 0.7, -0.2, 0, Math.PI * 2);
	dc.fill(0xffffff);
	dc.stroke(0xdddddd, 2);

	// Right ear
	dc.beginPath();
	dc.ellipse(r * 0.4, -r * 1.2, r * 0.2, r * 0.7, 0.2, 0, Math.PI * 2);
	dc.fill(0xffffff);
	dc.stroke(0xdddddd, 2);

	// Inner pink
	dc.beginPath();
	dc.ellipse(-r * 0.4, -r * 1.2, r * 0.1, r * 0.5, -0.2, 0, Math.PI * 2);
	dc.fill(0xffb6c1);

	dc.beginPath();
	dc.ellipse(r * 0.4, -r * 1.2, r * 0.1, r * 0.5, 0.2, 0, Math.PI * 2);
	dc.fill(0xffb6c1);
}

/** Draw devil horns accessory */
export function drawDevilHorns(dc: DrawingContext, r: number): void {
	// Left horn
	dc.beginPath();
	dc.moveTo(-r * 0.6, -r * 0.3);
	dc.quadraticCurveTo(-r * 0.9, -r * 0.8, -r * 0.7, -r * 1.4);
	dc.quadraticCurveTo(-r * 0.5, -r * 0.9, -r * 0.3, -r * 0.4);
	dc.closePath();
	dc.fill(0x8b0000);
	dc.stroke(0x5c0000, 2);

	// Right horn
	dc.beginPath();
	dc.moveTo(r * 0.6, -r * 0.3);
	dc.quadraticCurveTo(r * 0.9, -r * 0.8, r * 0.7, -r * 1.4);
	dc.quadraticCurveTo(r * 0.5, -r * 0.9, r * 0.3, -r * 0.4);
	dc.closePath();
	dc.fill(0x8b0000);
	dc.stroke(0x5c0000, 2);
}

/** Draw halo accessory */
export function drawHalo(dc: DrawingContext, r: number): void {
	// Outer glow
	dc.beginPath();
	dc.ellipse(0, -r * 1.3, r * 0.8, r * 0.2, 0, 0, Math.PI * 2);
	dc.fill(0xffdf00, 0.3);

	// Main halo
	dc.beginPath();
	dc.ellipse(0, -r * 1.3, r * 0.7, r * 0.15, 0, 0, Math.PI * 2);
	dc.stroke(0xffd700, r * 0.15);

	// Inner shine
	dc.beginPath();
	dc.ellipse(0, -r * 1.35, r * 0.5, r * 0.08, 0, 0, Math.PI * 2);
	dc.stroke(0xffee88, r * 0.05);
}

/** Draw antenna accessory */
export function drawAntenna(dc: DrawingContext, r: number): void {
	// Stalk
	dc.beginPath();
	dc.moveTo(0, -r * 0.5);
	dc.lineTo(0, -r * 2.0);
	dc.stroke(0x666666, r * 0.08);

	// Glowing ball
	dc.circle(0, -r * 2.0, r * 0.25);
	dc.fill(0x00ff00);
	dc.circle(0, -r * 2.0, r * 0.18);
	dc.fill(0xaaffaa, 0.8);
}

/** Draw flower crown accessory */
export function drawFlowerCrown(dc: DrawingContext, r: number): void {
	const flowerPositions = [
		{ x: -r * 0.8, y: -r * 0.6 },
		{ x: -r * 0.4, y: -r * 0.9 },
		{ x: 0, y: -r * 1.0 },
		{ x: r * 0.4, y: -r * 0.9 },
		{ x: r * 0.8, y: -r * 0.6 },
	];
	const flowerColors = [0xff69b4, 0xffb6c1, 0xff1493, 0xffc0cb, 0xff69b4];

	// Green vine
	dc.beginPath();
	dc.arc(0, 0, r * 0.95, -Math.PI * 0.75, Math.PI * 0.75, true);
	dc.stroke(0x228b22, r * 0.1);

	// Draw flowers
	flowerPositions.forEach((pos, i) => {
		// Petals
		for (let p = 0; p < 5; p++) {
			const angle = (p / 5) * Math.PI * 2;
			const px = pos.x + Math.cos(angle) * r * 0.15;
			const py = pos.y + Math.sin(angle) * r * 0.15;
			dc.circle(px, py, r * 0.12);
			dc.fill(flowerColors[i] ?? 0xff69b4);
		}
		// Center
		dc.circle(pos.x, pos.y, r * 0.08);
		dc.fill(0xffff00);
	});
}

/** Draw star accessory */
export function drawStarAccessory(dc: DrawingContext, r: number): void {
	drawStar(dc, 0, -r * 1.2, r * 0.5, 0xffd700);
	drawStar(dc, 0, -r * 1.2, r * 0.3, 0xffee88);
}

/** Draw heart accessory */
export function drawHeart(dc: DrawingContext, r: number): void {
	dc.beginPath();
	dc.moveTo(0, -r * 0.8);
	dc.bezierCurveTo(-r * 0.5, -r * 1.4, -r * 1.0, -r * 0.9, 0, -r * 0.2);
	dc.bezierCurveTo(r * 1.0, -r * 0.9, r * 0.5, -r * 1.4, 0, -r * 0.8);
	dc.fill(0xff1493);

	// Shine
	dc.circle(-r * 0.25, -r * 1.0, r * 0.12);
	dc.fill(0xffffff, 0.5);
}

/** Draw bow tie accessory */
export function drawBowTie(dc: DrawingContext, r: number): void {
	// Left bow
	dc.beginPath();
	dc.moveTo(0, r * 0.8);
	dc.quadraticCurveTo(-r * 0.3, r * 0.5, -r * 0.6, r * 0.6);
	dc.quadraticCurveTo(-r * 0.3, r * 0.8, 0, r * 0.8);
	dc.quadraticCurveTo(-r * 0.3, r * 1.1, -r * 0.6, r * 1.0);
	dc.quadraticCurveTo(-r * 0.3, r * 1.1, 0, r * 0.8);
	dc.fill(0xff0000);

	// Right bow
	dc.beginPath();
	dc.moveTo(0, r * 0.8);
	dc.quadraticCurveTo(r * 0.3, r * 0.5, r * 0.6, r * 0.6);
	dc.quadraticCurveTo(r * 0.3, r * 0.8, 0, r * 0.8);
	dc.quadraticCurveTo(r * 0.3, r * 1.1, r * 0.6, r * 1.0);
	dc.quadraticCurveTo(r * 0.3, r * 1.1, 0, r * 0.8);
	dc.fill(0xff0000);

	// Center knot
	dc.circle(0, r * 0.8, r * 0.12);
	dc.fill(0xcc0000);
}

/** Draw baseball cap accessory */
export function drawBaseballCap(dc: DrawingContext, r: number): void {
	// Cap dome
	dc.beginPath();
	dc.arc(0, -r * 0.4, r * 0.8, Math.PI, 0, false);
	dc.closePath();
	dc.fill(0x1e90ff);
	dc.stroke(0x1565c0, 2);

	// Bill/visor
	dc.beginPath();
	dc.ellipse(0, -r * 0.35, r * 1.1, r * 0.25, 0, 0, Math.PI);
	dc.fill(0x1565c0);
	dc.stroke(0x0d47a1, 2);

	// Button on top
	dc.circle(0, -r * 1.2, r * 0.1);
	dc.fill(0xffffff);
}

/** Draw beanie accessory */
export function drawBeanie(dc: DrawingContext, r: number): void {
	// Main beanie body
	dc.beginPath();
	dc.moveTo(-r * 0.85, -r * 0.2);
	dc.quadraticCurveTo(-r * 0.9, -r * 0.8, -r * 0.5, -r * 1.2);
	dc.quadraticCurveTo(0, -r * 1.5, r * 0.5, -r * 1.2);
	dc.quadraticCurveTo(r * 0.9, -r * 0.8, r * 0.85, -r * 0.2);
	dc.closePath();
	dc.fill(0xff6b6b);
	dc.stroke(0xcc5555, 2);

	// Ribbed brim
	dc.beginPath();
	dc.moveTo(-r * 0.85, -r * 0.2);
	dc.quadraticCurveTo(0, -r * 0.4, r * 0.85, -r * 0.2);
	dc.quadraticCurveTo(0, 0, -r * 0.85, -r * 0.2);
	dc.fill(0xcc5555);

	// Pom pom
	dc.circle(0, -r * 1.4, r * 0.25);
	dc.fill(0xffffff);
}

/** Draw propeller hat accessory */
export function drawPropellerHat(dc: DrawingContext, r: number): void {
	// Cap dome
	dc.beginPath();
	dc.arc(0, -r * 0.4, r * 0.75, Math.PI, 0, false);
	dc.closePath();
	dc.fill(0xffcc00);
	dc.stroke(0xdd9900, 2);

	// Stripes on cap
	dc.beginPath();
	dc.moveTo(-r * 0.5, -r * 0.4);
	dc.lineTo(-r * 0.25, -r * 1.1);
	dc.stroke(0xff0000, r * 0.15);

	dc.beginPath();
	dc.moveTo(r * 0.5, -r * 0.4);
	dc.lineTo(r * 0.25, -r * 1.1);
	dc.stroke(0xff0000, r * 0.15);

	// Propeller base
	dc.circle(0, -r * 1.15, r * 0.15);
	dc.fill(0x888888);

	// Propeller blades
	const bladeColors: number[] = [0xff0000, 0x00ff00, 0x0000ff, 0xffff00];
	for (let i = 0; i < 4; i++) {
		const angle = (i * Math.PI) / 2;
		dc.beginPath();
		dc.moveTo(0, -r * 1.15);
		dc.lineTo(
			Math.cos(angle) * r * 0.5,
			-r * 1.15 + Math.sin(angle) * r * 0.15
		);
		dc.lineTo(
			Math.cos(angle + 0.3) * r * 0.4,
			-r * 1.15 + Math.sin(angle + 0.3) * r * 0.12
		);
		dc.closePath();
		dc.fill(bladeColors[i] ?? 0xff0000);
	}
}

/** Draw graduation cap accessory */
export function drawGraduationCap(dc: DrawingContext, r: number): void {
	// Mortarboard top (diamond shape from above)
	dc.beginPath();
	dc.moveTo(0, -r * 1.3);
	dc.lineTo(-r * 1.1, -r * 0.9);
	dc.lineTo(0, -r * 0.5);
	dc.lineTo(r * 1.1, -r * 0.9);
	dc.closePath();
	dc.fill(0x1a1a1a);
	dc.stroke(0x333333, 2);

	// Cap base
	dc.beginPath();
	dc.ellipse(0, -r * 0.4, r * 0.7, r * 0.2, 0, 0, Math.PI * 2);
	dc.fill(0x1a1a1a);

	// Tassel button
	dc.circle(0, -r * 0.9, r * 0.1);
	dc.fill(0xffd700);

	// Tassel string
	dc.beginPath();
	dc.moveTo(0, -r * 0.8);
	dc.quadraticCurveTo(r * 0.5, -r * 0.5, r * 0.6, -r * 0.1);
	dc.stroke(0xffd700, 3);

	// Tassel end
	dc.beginPath();
	dc.moveTo(r * 0.55, -r * 0.1);
	dc.lineTo(r * 0.65, r * 0.2);
	dc.lineTo(r * 0.55, r * 0.15);
	dc.lineTo(r * 0.6, r * 0.25);
	dc.stroke(0xffd700, 2);
}

/** Draw hard hat accessory */
export function drawHardHat(dc: DrawingContext, r: number): void {
	// Main dome
	dc.beginPath();
	dc.arc(0, -r * 0.5, r * 0.9, Math.PI, 0, false);
	dc.closePath();
	dc.fill(0xffcc00);
	dc.stroke(0xdd9900, 2);

	// Brim
	dc.beginPath();
	dc.ellipse(0, -r * 0.45, r * 1.05, r * 0.18, 0, 0, Math.PI * 2);
	dc.fill(0xeebb00);
	dc.stroke(0xcc9900, 2);

	// Ridge on top
	dc.beginPath();
	dc.moveTo(-r * 0.6, -r * 0.7);
	dc.quadraticCurveTo(0, -r * 1.0, r * 0.6, -r * 0.7);
	dc.stroke(0xdd9900, 4);

	// Highlight
	dc.beginPath();
	dc.arc(-r * 0.3, -r * 0.9, r * 0.15, 0, Math.PI * 2);
	dc.fill(0xffee88, 0.5);
}

/** Draw fez accessory */
export function drawFez(dc: DrawingContext, r: number): void {
	// Main fez body (truncated cone)
	dc.beginPath();
	dc.moveTo(-r * 0.6, -r * 0.3);
	dc.lineTo(-r * 0.45, -r * 1.2);
	dc.lineTo(r * 0.45, -r * 1.2);
	dc.lineTo(r * 0.6, -r * 0.3);
	dc.closePath();
	dc.fill(0xcc0000);
	dc.stroke(0x990000, 2);

	// Flat top
	dc.beginPath();
	dc.ellipse(0, -r * 1.2, r * 0.45, r * 0.1, 0, 0, Math.PI * 2);
	dc.fill(0xcc0000);
	dc.stroke(0x990000, 2);

	// Tassel attachment
	dc.circle(0, -r * 1.2, r * 0.08);
	dc.fill(0x000000);

	// Tassel string
	dc.beginPath();
	dc.moveTo(0, -r * 1.15);
	dc.quadraticCurveTo(r * 0.4, -r * 0.9, r * 0.5, -r * 0.5);
	dc.stroke(0x000000, 2);

	// Tassel end
	dc.beginPath();
	dc.moveTo(r * 0.45, -r * 0.5);
	dc.lineTo(r * 0.55, -r * 0.2);
	dc.lineTo(r * 0.5, -r * 0.25);
	dc.lineTo(r * 0.52, -r * 0.15);
	dc.stroke(0x000000, 2);
}

/** Draw beret accessory */
export function drawBeret(dc: DrawingContext, r: number): void {
	// Main beret body
	dc.beginPath();
	dc.moveTo(-r * 0.9, -r * 0.35);
	dc.quadraticCurveTo(-r * 1.1, -r * 0.8, -r * 0.3, -r * 1.0);
	dc.quadraticCurveTo(r * 0.4, -r * 1.1, r * 0.8, -r * 0.7);
	dc.quadraticCurveTo(r * 0.9, -r * 0.4, r * 0.7, -r * 0.35);
	dc.closePath();
	dc.fill(0x1a1a1a);
	dc.stroke(0x333333, 2);

	// Headband
	dc.beginPath();
	dc.ellipse(0, -r * 0.35, r * 0.8, r * 0.15, 0, 0, Math.PI * 2);
	dc.fill(0x1a1a1a);

	// Stem/nub on top
	dc.circle(-r * 0.1, -r * 0.95, r * 0.08);
	dc.fill(0x1a1a1a);
}

/** Draw sombrero accessory */
export function drawSombrero(dc: DrawingContext, r: number): void {
	// Wide brim shadow
	dc.beginPath();
	dc.ellipse(0, -r * 0.25, r * 1.8, r * 0.35, 0, 0, Math.PI * 2);
	dc.fill(0xb8860b);

	// Wide brim
	dc.beginPath();
	dc.ellipse(0, -r * 0.35, r * 1.7, r * 0.3, 0, 0, Math.PI * 2);
	dc.fill(0xdaa520);
	dc.stroke(0xb8860b, 2);

	// Dome
	dc.beginPath();
	dc.arc(0, -r * 0.5, r * 0.6, Math.PI, 0, false);
	dc.closePath();
	dc.fill(0xdaa520);
	dc.stroke(0xb8860b, 2);

	// Decorative band
	dc.beginPath();
	dc.ellipse(0, -r * 0.5, r * 0.62, r * 0.12, 0, 0, Math.PI * 2);
	dc.fill(0xff4500);

	// Zigzag pattern on band
	for (let i = -3; i <= 3; i++) {
		dc.beginPath();
		dc.moveTo(i * r * 0.18, -r * 0.55);
		dc.lineTo(i * r * 0.18 + r * 0.09, -r * 0.45);
		dc.stroke(0xffd700, 2);
	}
}

/** Draw santa hat accessory */
export function drawSantaHat(dc: DrawingContext, r: number): void {
	// Main hat cone
	dc.beginPath();
	dc.moveTo(-r * 0.8, -r * 0.25);
	dc.quadraticCurveTo(-r * 0.3, -r * 1.2, r * 0.7, -r * 1.8);
	dc.quadraticCurveTo(r * 0.5, -r * 1.0, r * 0.8, -r * 0.25);
	dc.closePath();
	dc.fill(0xcc0000);
	dc.stroke(0x990000, 2);

	// White fur trim
	dc.beginPath();
	dc.ellipse(0, -r * 0.25, r * 0.9, r * 0.2, 0, 0, Math.PI * 2);
	dc.fill(0xffffff);
	dc.stroke(0xeeeeee, 2);

	// Pom pom
	dc.circle(r * 0.7, -r * 1.8, r * 0.25);
	dc.fill(0xffffff);
}

/** Draw sailor hat accessory */
export function drawSailorHat(dc: DrawingContext, r: number): void {
	// Main white cap
	dc.beginPath();
	dc.arc(0, -r * 0.5, r * 0.75, Math.PI, 0, false);
	dc.closePath();
	dc.fill(0xffffff);
	dc.stroke(0xdddddd, 2);

	// Upturned brim
	dc.beginPath();
	dc.ellipse(0, -r * 0.4, r * 0.9, r * 0.2, 0, 0, Math.PI);
	dc.fill(0xffffff);
	dc.stroke(0xdddddd, 2);

	// Blue band
	dc.beginPath();
	dc.moveTo(-r * 0.7, -r * 0.55);
	dc.quadraticCurveTo(0, -r * 0.7, r * 0.7, -r * 0.55);
	dc.stroke(0x000080, r * 0.15);

	// Anchor emblem
	dc.beginPath();
	dc.moveTo(0, -r * 0.75);
	dc.lineTo(0, -r * 0.45);
	dc.stroke(0xffd700, 2);
	dc.beginPath();
	dc.moveTo(-r * 0.1, -r * 0.5);
	dc.lineTo(r * 0.1, -r * 0.5);
	dc.stroke(0xffd700, 2);
	dc.circle(0, -r * 0.78, r * 0.05);
	dc.fill(0xffd700);
}

/** Draw fedora accessory */
export function drawFedora(dc: DrawingContext, r: number): void {
	// Brim shadow
	dc.beginPath();
	dc.ellipse(0, -r * 0.35, r * 1.2, r * 0.22, 0, 0, Math.PI * 2);
	dc.fill(0x2a1a0a);

	// Brim
	dc.beginPath();
	dc.ellipse(0, -r * 0.4, r * 1.15, r * 0.2, 0, 0, Math.PI * 2);
	dc.fill(0x4a3728);
	dc.stroke(0x3a2718, 2);

	// Crown
	dc.beginPath();
	dc.moveTo(-r * 0.6, -r * 0.4);
	dc.lineTo(-r * 0.55, -r * 1.1);
	dc.quadraticCurveTo(-r * 0.3, -r * 1.25, 0, -r * 1.1);
	dc.quadraticCurveTo(r * 0.3, -r * 1.25, r * 0.55, -r * 1.1);
	dc.lineTo(r * 0.6, -r * 0.4);
	dc.closePath();
	dc.fill(0x4a3728);
	dc.stroke(0x3a2718, 2);

	// Hat band
	dc.beginPath();
	dc.moveTo(-r * 0.58, -r * 0.55);
	dc.lineTo(r * 0.58, -r * 0.55);
	dc.stroke(0x1a1a1a, r * 0.1);
}

/** Draw mushroom cap accessory */
export function drawMushroomCap(dc: DrawingContext, r: number): void {
	// Main cap dome
	dc.beginPath();
	dc.arc(0, -r * 0.3, r * 1.0, Math.PI, 0, false);
	dc.closePath();
	dc.fill(0xff0000);
	dc.stroke(0xcc0000, 2);

	// White spots
	dc.circle(-r * 0.5, -r * 0.7, r * 0.18);
	dc.fill(0xffffff);
	dc.circle(r * 0.3, -r * 0.85, r * 0.15);
	dc.fill(0xffffff);
	dc.circle(r * 0.55, -r * 0.5, r * 0.12);
	dc.fill(0xffffff);
	dc.circle(-r * 0.15, -r * 1.0, r * 0.13);
	dc.fill(0xffffff);
	dc.circle(-r * 0.7, -r * 0.4, r * 0.1);
	dc.fill(0xffffff);

	// Underside
	dc.beginPath();
	dc.ellipse(0, -r * 0.3, r * 1.0, r * 0.15, 0, 0, Math.PI);
	dc.fill(0xffeecc);
}

/** Draw knight helmet accessory */
export function drawKnightHelmet(dc: DrawingContext, r: number): void {
	// Main helmet dome
	dc.beginPath();
	dc.arc(0, -r * 0.3, r * 0.9, Math.PI, 0, false);
	dc.closePath();
	dc.fill(0x888888);
	dc.stroke(0x666666, 2);

	// Face guard / visor
	dc.beginPath();
	dc.moveTo(-r * 0.7, -r * 0.3);
	dc.lineTo(-r * 0.5, r * 0.1);
	dc.lineTo(r * 0.5, r * 0.1);
	dc.lineTo(r * 0.7, -r * 0.3);
	dc.closePath();
	dc.fill(0x777777);
	dc.stroke(0x555555, 2);

	// Visor slits
	for (let i = -2; i <= 2; i++) {
		dc.beginPath();
		dc.moveTo(i * r * 0.15, -r * 0.15);
		dc.lineTo(i * r * 0.15, r * 0.0);
		dc.stroke(0x222222, 2);
	}

	// Plume holder
	dc.beginPath();
	dc.roundRect(-r * 0.1, -r * 1.2, r * 0.2, r * 0.15, r * 0.05);
	dc.fill(0x666666);

	// Red plume
	dc.beginPath();
	dc.moveTo(0, -r * 1.2);
	dc.quadraticCurveTo(-r * 0.3, -r * 1.8, 0, -r * 2.2);
	dc.quadraticCurveTo(r * 0.3, -r * 1.8, 0, -r * 1.2);
	dc.fill(0xff0000);
	dc.stroke(0xcc0000, 2);
}

/** Draw astronaut helmet accessory */
export function drawAstronautHelmet(dc: DrawingContext, r: number): void {
	// Helmet dome (glass bubble)
	dc.beginPath();
	dc.arc(0, -r * 0.2, r * 1.1, 0, Math.PI * 2);
	dc.fill(0xffffff, 0.3);
	dc.stroke(0xcccccc, 3);

	// Helmet frame
	dc.beginPath();
	dc.arc(0, -r * 0.2, r * 1.15, Math.PI * 0.8, Math.PI * 0.2, true);
	dc.stroke(0xffffff, 6);

	// Visor (reflective blue tint)
	dc.beginPath();
	dc.arc(0, -r * 0.1, r * 0.85, Math.PI * 0.3, Math.PI * 0.7, true);
	dc.fill(0x4488ff, 0.6);

	// Reflection on visor
	dc.beginPath();
	dc.arc(-r * 0.3, -r * 0.4, r * 0.2, 0, Math.PI * 2);
	dc.fill(0xffffff, 0.4);

	// Side vents
	dc.circle(-r * 0.9, -r * 0.1, r * 0.12);
	dc.fill(0x888888);
	dc.circle(r * 0.9, -r * 0.1, r * 0.12);
	dc.fill(0x888888);
}

/** Draw jester hat accessory */
export function drawJesterHat(dc: DrawingContext, r: number): void {
	// Base band
	dc.beginPath();
	dc.ellipse(0, -r * 0.3, r * 0.85, r * 0.15, 0, 0, Math.PI * 2);
	dc.fill(0xffd700);

	// Left prong (purple)
	dc.beginPath();
	dc.moveTo(-r * 0.7, -r * 0.3);
	dc.quadraticCurveTo(-r * 1.3, -r * 1.0, -r * 1.0, -r * 1.8);
	dc.quadraticCurveTo(-r * 0.8, -r * 1.2, -r * 0.3, -r * 0.4);
	dc.closePath();
	dc.fill(0x9932cc);
	dc.stroke(0x7722aa, 2);

	// Middle prong (gold)
	dc.beginPath();
	dc.moveTo(-r * 0.2, -r * 0.35);
	dc.quadraticCurveTo(0, -r * 1.5, 0, -r * 2.0);
	dc.quadraticCurveTo(0, -r * 1.5, r * 0.2, -r * 0.35);
	dc.closePath();
	dc.fill(0xffd700);
	dc.stroke(0xccaa00, 2);

	// Right prong (purple)
	dc.beginPath();
	dc.moveTo(r * 0.7, -r * 0.3);
	dc.quadraticCurveTo(r * 1.3, -r * 1.0, r * 1.0, -r * 1.8);
	dc.quadraticCurveTo(r * 0.8, -r * 1.2, r * 0.3, -r * 0.4);
	dc.closePath();
	dc.fill(0x9932cc);
	dc.stroke(0x7722aa, 2);

	// Bells
	dc.circle(-r * 1.0, -r * 1.8, r * 0.15);
	dc.fill(0xffd700);
	dc.circle(0, -r * 2.0, r * 0.15);
	dc.fill(0xffd700);
	dc.circle(r * 1.0, -r * 1.8, r * 0.15);
	dc.fill(0xffd700);
}

/** Draw straw hat accessory */
export function drawStrawHat(dc: DrawingContext, r: number): void {
	// Wide brim shadow
	dc.beginPath();
	dc.ellipse(0, -r * 0.25, r * 1.6, r * 0.35, 0, 0, Math.PI * 2);
	dc.fill(0xc4a35a);

	// Wide brim
	dc.beginPath();
	dc.ellipse(0, -r * 0.35, r * 1.5, r * 0.3, 0, 0, Math.PI * 2);
	dc.fill(0xf4d794);
	dc.stroke(0xc4a35a, 2);

	// Dome
	dc.beginPath();
	dc.arc(0, -r * 0.5, r * 0.65, Math.PI, 0, false);
	dc.closePath();
	dc.fill(0xf4d794);
	dc.stroke(0xc4a35a, 2);

	// Straw texture lines
	for (let i = -2; i <= 2; i++) {
		dc.beginPath();
		dc.moveTo(i * r * 0.2, -r * 0.5);
		dc.lineTo(i * r * 0.15, -r * 1.1);
		dc.stroke(0xc4a35a, 1);
	}

	// Red ribbon band
	dc.beginPath();
	dc.ellipse(0, -r * 0.5, r * 0.67, r * 0.12, 0, 0, Math.PI * 2);
	dc.fill(0xcc0000);
}

/** Draw ushanka (Russian fur hat) accessory */
export function drawUshanka(dc: DrawingContext, r: number): void {
	// Main hat body
	dc.beginPath();
	dc.arc(0, -r * 0.5, r * 0.8, Math.PI, 0, false);
	dc.closePath();
	dc.fill(0x8b4513);
	dc.stroke(0x654321, 2);

	// Fur trim (white/gray)
	dc.beginPath();
	dc.ellipse(0, -r * 0.35, r * 0.9, r * 0.25, 0, 0, Math.PI * 2);
	dc.fill(0xd3d3d3);

	// Left ear flap
	dc.beginPath();
	dc.moveTo(-r * 0.6, -r * 0.35);
	dc.quadraticCurveTo(-r * 1.0, -r * 0.2, -r * 0.9, r * 0.3);
	dc.quadraticCurveTo(-r * 0.7, r * 0.1, -r * 0.5, -r * 0.2);
	dc.closePath();
	dc.fill(0x8b4513);
	dc.stroke(0xd3d3d3, 3);

	// Right ear flap
	dc.beginPath();
	dc.moveTo(r * 0.6, -r * 0.35);
	dc.quadraticCurveTo(r * 1.0, -r * 0.2, r * 0.9, r * 0.3);
	dc.quadraticCurveTo(r * 0.7, r * 0.1, r * 0.5, -r * 0.2);
	dc.closePath();
	dc.fill(0x8b4513);
	dc.stroke(0xd3d3d3, 3);

	// Red star emblem
	drawStar(dc, 0, -r * 0.85, r * 0.2, 0xff0000);
}

/** Draw mohawk accessory */
export function drawMohawk(dc: DrawingContext, r: number): void {
	// Mohawk spikes (gradient from front to back)
	const spikeColors: number[] = [0xff0000, 0xff4400, 0xff8800, 0xffcc00, 0xffff00];

	for (let i = 0; i < 5; i++) {
		const xOffset = (i - 2) * r * 0.35;
		const height = r * (1.8 - Math.abs(i - 2) * 0.25);

		dc.beginPath();
		dc.moveTo(xOffset - r * 0.15, -r * 0.3);
		dc.lineTo(xOffset, -height);
		dc.lineTo(xOffset + r * 0.15, -r * 0.3);
		dc.closePath();
		dc.fill(spikeColors[i] ?? 0xff0000);
		dc.stroke(0x880000, 2);
	}

	// Base strip
	dc.beginPath();
	dc.roundRect(-r * 0.85, -r * 0.35, r * 1.7, r * 0.15, r * 0.05);
	dc.fill(0x333333);
}

/** Draw tiara accessory */
export function drawTiara(dc: DrawingContext, r: number): void {
	// Base band (silver)
	dc.beginPath();
	dc.ellipse(0, -r * 0.35, r * 0.8, r * 0.12, 0, Math.PI, 0);
	dc.stroke(0xc0c0c0, 4);

	// Center peak
	dc.beginPath();
	dc.moveTo(-r * 0.15, -r * 0.4);
	dc.lineTo(0, -r * 1.3);
	dc.lineTo(r * 0.15, -r * 0.4);
	dc.closePath();
	dc.fill(0xc0c0c0);
	dc.stroke(0xa0a0a0, 2);

	// Side peaks
	dc.beginPath();
	dc.moveTo(-r * 0.45, -r * 0.38);
	dc.lineTo(-r * 0.35, -r * 0.9);
	dc.lineTo(-r * 0.25, -r * 0.38);
	dc.closePath();
	dc.fill(0xc0c0c0);

	dc.beginPath();
	dc.moveTo(r * 0.45, -r * 0.38);
	dc.lineTo(r * 0.35, -r * 0.9);
	dc.lineTo(r * 0.25, -r * 0.38);
	dc.closePath();
	dc.fill(0xc0c0c0);

	// Center gem (pink diamond)
	dc.beginPath();
	dc.moveTo(0, -r * 0.6);
	dc.lineTo(-r * 0.12, -r * 0.8);
	dc.lineTo(0, -r * 1.0);
	dc.lineTo(r * 0.12, -r * 0.8);
	dc.closePath();
	dc.fill(0xff69b4);
	dc.stroke(0xff1493, 1);

	// Side gems
	dc.circle(-r * 0.35, -r * 0.65, r * 0.08);
	dc.fill(0x00ffff);
	dc.circle(r * 0.35, -r * 0.65, r * 0.08);
	dc.fill(0x00ffff);

	// Sparkles
	dc.circle(-r * 0.55, -r * 0.5, r * 0.03);
	dc.fill(0xffffff);
	dc.circle(r * 0.55, -r * 0.5, r * 0.03);
	dc.fill(0xffffff);
	dc.circle(0, -r * 1.15, r * 0.04);
	dc.fill(0xffffff);
}

/** Draw bowler hat accessory */
export function drawBowlerHat(dc: DrawingContext, r: number): void {
	// Brim shadow
	dc.beginPath();
	dc.ellipse(0, -r * 0.35, r * 1.1, r * 0.2, 0, 0, Math.PI * 2);
	dc.fill(0x0a0a0a);

	// Brim
	dc.beginPath();
	dc.ellipse(0, -r * 0.4, r * 1.05, r * 0.18, 0, 0, Math.PI * 2);
	dc.fill(0x1a1a1a);
	dc.stroke(0x333333, 2);

	// Rounded dome
	dc.beginPath();
	dc.arc(0, -r * 0.6, r * 0.65, Math.PI, 0, false);
	dc.closePath();
	dc.fill(0x1a1a1a);
	dc.stroke(0x333333, 2);

	// Hat band
	dc.beginPath();
	dc.ellipse(0, -r * 0.5, r * 0.67, r * 0.1, 0, 0, Math.PI * 2);
	dc.fill(0x4a3728);
}

/** Draw football helmet accessory */
export function drawFootballHelmet(dc: DrawingContext, r: number): void {
	// Main helmet dome
	dc.beginPath();
	dc.arc(0, -r * 0.3, r * 0.95, Math.PI, 0, false);
	dc.closePath();
	dc.fill(0x1e3a5f);
	dc.stroke(0x0f2540, 3);

	// Face mask
	dc.beginPath();
	dc.moveTo(-r * 0.6, -r * 0.2);
	dc.lineTo(-r * 0.8, r * 0.2);
	dc.lineTo(r * 0.8, r * 0.2);
	dc.lineTo(r * 0.6, -r * 0.2);
	dc.stroke(0x888888, 4);

	// Horizontal bars
	dc.beginPath();
	dc.moveTo(-r * 0.7, 0);
	dc.lineTo(r * 0.7, 0);
	dc.stroke(0x888888, 3);

	// Center stripe
	dc.beginPath();
	dc.moveTo(0, -r * 1.25);
	dc.lineTo(0, -r * 0.4);
	dc.stroke(0xffffff, r * 0.12);

	// Side decal (star)
	drawStar(dc, -r * 0.5, -r * 0.7, r * 0.15, 0xffffff);
}

/** Draw afro accessory */
export function drawAfro(dc: DrawingContext, r: number): void {
	// Main afro shape (big fluffy circle)
	dc.beginPath();
	dc.arc(0, -r * 0.6, r * 1.3, 0, Math.PI * 2);
	dc.fill(0x2a1a0a);

	// Texture bumps for fluffy effect
	const bumpAngles = [0, 0.5, 1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5, 5.0, 5.5];
	for (const angle of bumpAngles) {
		const bx = Math.cos(angle) * r * 1.2;
		const by = -r * 0.6 + Math.sin(angle) * r * 1.2;
		dc.circle(bx, by, r * 0.25);
		dc.fill(0x2a1a0a);
	}

	// Inner shadow for depth
	dc.beginPath();
	dc.arc(0, -r * 0.4, r * 0.7, 0, Math.PI * 2);
	dc.fill(0x1a0a00, 0.3);

	// Pick comb sticking out
	dc.beginPath();
	dc.moveTo(r * 0.8, -r * 1.2);
	dc.lineTo(r * 0.85, -r * 0.6);
	dc.lineTo(r * 0.95, -r * 0.6);
	dc.lineTo(r * 0.9, -r * 1.3);
	dc.closePath();
	dc.fill(0xff6b00);

	// Pick teeth
	for (let i = 0; i < 3; i++) {
		dc.beginPath();
		dc.moveTo(r * 0.83 + i * 0.04 * r, -r * 0.6);
		dc.lineTo(r * 0.83 + i * 0.04 * r, -r * 0.45);
		dc.stroke(0xff6b00, 2);
	}
}

/** Draw shower cap accessory */
export function drawShowerCap(dc: DrawingContext, r: number): void {
	// Main cap dome (translucent plastic look)
	dc.beginPath();
	dc.arc(0, -r * 0.4, r * 1.0, Math.PI, 0, false);
	dc.closePath();
	dc.fill(0xffb6c1, 0.7);
	dc.stroke(0xff69b4, 2);

	// Elastic ruffle edge
	const rufflePoints = 16;
	dc.beginPath();
	for (let i = 0; i <= rufflePoints; i++) {
		const angle = Math.PI + (i / rufflePoints) * Math.PI;
		const ruffleR = r * 1.05 + Math.sin(i * 3) * r * 0.08;
		const x = Math.cos(angle) * ruffleR;
		const y = -r * 0.35 + Math.sin(angle) * r * 0.15;
		if (i === 0) dc.moveTo(x, y);
		else dc.lineTo(x, y);
	}
	dc.stroke(0xff69b4, 3);

	// Polka dots
	dc.circle(-r * 0.4, -r * 0.8, r * 0.12);
	dc.fill(0xffffff, 0.5);
	dc.circle(r * 0.3, -r * 0.9, r * 0.1);
	dc.fill(0xffffff, 0.5);
	dc.circle(r * 0.5, -r * 0.6, r * 0.08);
	dc.fill(0xffffff, 0.5);
	dc.circle(-r * 0.2, -r * 1.1, r * 0.09);
	dc.fill(0xffffff, 0.5);

	// Water droplet
	dc.beginPath();
	dc.moveTo(r * 0.7, -r * 0.5);
	dc.quadraticCurveTo(r * 0.8, -r * 0.3, r * 0.7, -r * 0.2);
	dc.quadraticCurveTo(r * 0.6, -r * 0.3, r * 0.7, -r * 0.5);
	dc.fill(0x87ceeb);
}

/**
 * Draw an accessory using the abstract drawing context
 */
export function drawAccessory(dc: DrawingContext, type: AccessoryType, radius: number): void {
	switch (type) {
		case AccessoryType.CROWN:
			drawCrown(dc, radius);
			break;
		case AccessoryType.PARTY_HAT:
			drawPartyHat(dc, radius);
			break;
		case AccessoryType.WIZARD_HAT:
			drawWizardHat(dc, radius);
			break;
		case AccessoryType.TOP_HAT:
			drawTopHat(dc, radius);
			break;
		case AccessoryType.PIRATE_HAT:
			drawPirateHat(dc, radius);
			break;
		case AccessoryType.VIKING_HELMET:
			drawVikingHelmet(dc, radius);
			break;
		case AccessoryType.COWBOY_HAT:
			drawCowboyHat(dc, radius);
			break;
		case AccessoryType.CHEF_HAT:
			drawChefHat(dc, radius);
			break;
		case AccessoryType.BANDANA:
			drawBandana(dc, radius);
			break;
		case AccessoryType.BASEBALL_CAP:
			drawBaseballCap(dc, radius);
			break;
		case AccessoryType.BEANIE:
			drawBeanie(dc, radius);
			break;
		case AccessoryType.PROPELLER_HAT:
			drawPropellerHat(dc, radius);
			break;
		case AccessoryType.GRADUATION_CAP:
			drawGraduationCap(dc, radius);
			break;
		case AccessoryType.HARD_HAT:
			drawHardHat(dc, radius);
			break;
		case AccessoryType.FEZ:
			drawFez(dc, radius);
			break;
		case AccessoryType.BERET:
			drawBeret(dc, radius);
			break;
		case AccessoryType.SOMBRERO:
			drawSombrero(dc, radius);
			break;
		case AccessoryType.SANTA_HAT:
			drawSantaHat(dc, radius);
			break;
		case AccessoryType.SAILOR_HAT:
			drawSailorHat(dc, radius);
			break;
		case AccessoryType.FEDORA:
			drawFedora(dc, radius);
			break;
		case AccessoryType.MUSHROOM_CAP:
			drawMushroomCap(dc, radius);
			break;
		case AccessoryType.KNIGHT_HELMET:
			drawKnightHelmet(dc, radius);
			break;
		case AccessoryType.ASTRONAUT_HELMET:
			drawAstronautHelmet(dc, radius);
			break;
		case AccessoryType.JESTER_HAT:
			drawJesterHat(dc, radius);
			break;
		case AccessoryType.STRAW_HAT:
			drawStrawHat(dc, radius);
			break;
		case AccessoryType.USHANKA:
			drawUshanka(dc, radius);
			break;
		case AccessoryType.MOHAWK:
			drawMohawk(dc, radius);
			break;
		case AccessoryType.TIARA:
			drawTiara(dc, radius);
			break;
		case AccessoryType.BOWLER_HAT:
			drawBowlerHat(dc, radius);
			break;
		case AccessoryType.FOOTBALL_HELMET:
			drawFootballHelmet(dc, radius);
			break;
		case AccessoryType.AFRO:
			drawAfro(dc, radius);
			break;
		case AccessoryType.SHOWER_CAP:
			drawShowerCap(dc, radius);
			break;
		case AccessoryType.SUNGLASSES:
			drawSunglasses(dc, radius);
			break;
		case AccessoryType.HEADPHONES:
			drawHeadphones(dc, radius);
			break;
		case AccessoryType.NINJA_MASK:
			drawNinjaMask(dc, radius);
			break;
		case AccessoryType.CAT_EARS:
			drawCatEars(dc, radius);
			break;
		case AccessoryType.BUNNY_EARS:
			drawBunnyEars(dc, radius);
			break;
		case AccessoryType.DEVIL_HORNS:
			drawDevilHorns(dc, radius);
			break;
		case AccessoryType.HALO:
			drawHalo(dc, radius);
			break;
		case AccessoryType.ANTENNA:
			drawAntenna(dc, radius);
			break;
		case AccessoryType.FLOWER_CROWN:
			drawFlowerCrown(dc, radius);
			break;
		// === EYES GROUP ===
		case AccessoryType.GLASSES_3D:
			drawGlasses3D(dc, radius);
			break;
		case AccessoryType.MONOCLE:
			drawMonocle(dc, radius);
			break;
		case AccessoryType.VR_HEADSET:
			drawVRHeadset(dc, radius);
			break;
		case AccessoryType.SKI_GOGGLES:
			drawSkiGoggles(dc, radius);
			break;
		case AccessoryType.AVIATORS:
			drawAviators(dc, radius);
			break;
		case AccessoryType.CYCLOPS_VISOR:
			drawCyclopsVisor(dc, radius);
			break;
		case AccessoryType.EYE_PATCH:
			drawEyePatch(dc, radius);
			break;
		case AccessoryType.CUCUMBER_SLICES:
			drawCucumberSlices(dc, radius);
			break;
		case AccessoryType.ANIME_EYES:
			drawAnimeEyes(dc, radius);
			break;

		// === MOUTH GROUP ===
		case AccessoryType.VAMPIRE_FANGS:
			drawVampireFangs(dc, radius);
			break;
		case AccessoryType.BUCK_TEETH:
			drawBuckTeeth(dc, radius);
			break;
		case AccessoryType.MUSTACHE:
			drawMustache(dc, radius);
			break;
		case AccessoryType.BEARD:
			drawBeard(dc, radius);
			break;
		case AccessoryType.MEDICAL_MASK:
			drawMedicalMask(dc, radius);
			break;
		case AccessoryType.CLOWN_NOSE:
			drawClownNose(dc, radius);
			break;
		case AccessoryType.BUBBLEGUM:
			drawBubblegum(dc, radius);
			break;
		case AccessoryType.PIPE:
			drawPipe(dc, radius);
			break;
		case AccessoryType.ZIPPER_MOUTH:
			drawZipperMouth(dc, radius);
			break;
		case AccessoryType.DOG_TONGUE:
			drawDogTongue(dc, radius);
			break;
		case AccessoryType.STAR:
			drawStarAccessory(dc, radius);
			break;
		case AccessoryType.HEART:
			drawHeart(dc, radius);
			break;
		case AccessoryType.BOW_TIE:
			drawBowTie(dc, radius);
			break;
	}
}
